import type { MegaJSON } from "@certa/studio-core/src/types/megaJSON.types";
import type { AppJSON } from "@certa/studio-core/src/types/appJSON.types";
import { processModelCreatorFromAppJSON } from "./process.model";
import { v4 as uuid } from "uuid";
import {
  forwardParseAppMetaJSONSync,
  forwardParseDataObjectAppJSON,
  reverseParseDataObjectAppJSON
} from "@certa/studio-parser";
import type {
  DataObjectAppJSON,
  DataObjectType
} from "@certa/types/src/dataObjects.types";

export const recordsMegaJSONModelCreatorFromAppJSON = (
  records: DataObjectAppJSON[]
) => {
  return records.reduce(
    (acc: Record<string, DataObjectType>, recordAppJSON) => {
      acc[recordAppJSON.tag] = reverseParseDataObjectAppJSON(recordAppJSON);
      return acc;
    },
    {}
  );
};

export const megaJSONModelCreatorFromAppJSON = (
  appJSON: AppJSON,
  { branchSavepointId }: { branchSavepointId: string | number }
) => {
  const megaJSON: MegaJSON = {
    id: `${appJSON.id ?? branchSavepointId}`,
    processes: {},
    base_app_settings: appJSON.base_app_settings,
    app_settings: appJSON.app_settings,
    records: recordsMegaJSONModelCreatorFromAppJSON(appJSON.records),
    __appConfigSaveId: appJSON.app_settings.__studio_save_id ?? uuid(),
    __saveId: uuid()
  };
  // migrate all processes to the latest version
  Object.entries(appJSON.processes).forEach(([id, process]) => {
    const metaJSONProcess = processModelCreatorFromAppJSON(process, {
      processId: id
    });
    megaJSON.processes[id] = metaJSONProcess;
  });

  return megaJSON;
};

export const recordsAppJSONModelCreatorFromMegaJSON = (
  records: Record<string, DataObjectType>
) => {
  return Object.values(records).map(record =>
    forwardParseDataObjectAppJSON(record)
  );
};

export const appJSONModelCreatorFromMegaJSON = (megaJSON: MegaJSON) => {
  const { __appConfigSaveId, __saveId, records, processes, ...rest } = megaJSON;
  const appJSON: AppJSON = {
    ...rest,
    records: Object.values(records).map(record =>
      forwardParseDataObjectAppJSON(record)
    ),
    processes: Object.entries(processes).reduce(
      (acc: AppJSON["processes"], [id, process]) => {
        acc[id] = forwardParseAppMetaJSONSync({ metaJSON: process });
        return acc;
      },
      {}
    )
  };
  return appJSON;
};
